import React from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import queryString from 'query-string'
import { isEmpty } from 'lodash'

import { AuthContainer } from '@/views/Auth/AuthContainer'
import { Banner } from '@/components/banners/Banner'

// Public landing for a provider's admin-consent / OAuth redirect. Whoever reaches it is the
// customer's workspace or tenant admin, who has no Balto account, so the page stays unauthenticated
// and purely presentational; no privileged action runs here. Adding a provider is just another
// PROVIDERS entry pointed at /integrations/<provider>/callback.
//
// By default success is inferred from the absence of an `error` param. A provider can instead
// require a positive success signal (`successParam`, e.g. Microsoft Entra requires
// `admin_consent=True`), supply its own success copy (`success`), and show a neutral state
// (`pending`) when the page is reached without that signal, so a bare visit never reports a false
// success. `reference` surfaces an id (e.g. tenant) for support.
const PROVIDERS = {
  sharepoint: {
    name: 'Microsoft SharePoint',
    reference: { param: 'tenant', label: 'Microsoft tenant ID' },
  },
  entra: {
    name: 'Microsoft Entra',
    successParam: 'admin_consent',
    success: {
      heading: 'Successfully added',
      body: 'The Balto application has been added to your Microsoft Entra tenant. You can close this window.',
    },
    pending: {
      heading: 'Finish installing Balto',
      body: 'Approve the Balto application from Microsoft to complete the install. This page confirms the install once Microsoft sends you back here.',
    },
  },
}

const FALLBACK_PROVIDER_NAME = 'Your knowledge base'
const DEFAULT_SUCCESS_BODY =
  'You can close this window. Our team will finish setting up your integration.'

const IntegrationCallbackPage = () => {
  const location = useLocation()
  const { provider } = useParams()
  const { error, ...params } = queryString.parse(location.search)

  const config = PROVIDERS[provider]
  const providerName = config ? config.name : FALLBACK_PROVIDER_NAME
  const referenceValue = config?.reference ? params[config.reference.param] : undefined

  const failed = !isEmpty(error)
  const hasSuccessSignal =
    isEmpty(config?.successParam) || String(params[config.successParam]).toLowerCase() === 'true'
  const connected = !failed && hasSuccessSignal
  const pending = !failed && !hasSuccessSignal

  const successHeading = isEmpty(config?.success)
    ? `${providerName} connected`
    : config.success.heading
  const successBody = isEmpty(config?.success) ? DEFAULT_SUCCESS_BODY : config.success.body

  return (
    <>
      <Helmet>
        <title>{providerName} | Balto Cloud</title>
      </Helmet>

      <AuthContainer>
        {connected && (
          <>
            <h2 data-testid="integration-callback-page">{successHeading}</h2>
            <p>{successBody}</p>
            {!isEmpty(referenceValue) && (
              <p>
                {config.reference.label}: {referenceValue}
              </p>
            )}
          </>
        )}

        {pending && (
          <>
            <h2 data-testid="integration-callback-page">{config.pending.heading}</h2>
            <p>{config.pending.body}</p>
          </>
        )}

        {!connected && !pending && (
          <>
            <h2 data-testid="integration-callback-page">
              We were unable to complete the connection
            </h2>
            <Banner critical>
              {providerName} consent was not completed. You can close this window, or ask your Balto
              contact to resend the link to try again.
            </Banner>
          </>
        )}
      </AuthContainer>
    </>
  )
}

export default IntegrationCallbackPage
